// ============================================================
// LEVELS — 單機關卡資料（取代手動佈陣）
// ============================================================
//
// 單機流程改為「選關卡」：點單機 → 關卡選擇（GS.LEVELS）→ 選一關後
// 玩家士兵單位與起始位置、AI 士兵與 AI 牌組全部固定，玩家只需選自己的
// 牌組並規劃各單位路徑（Deployment 用 PRESET_RULES：不能拖移/增減單位，
// 只能畫路徑）。PvP 仍走手動佈陣（DEFAULT_MANUAL_RULES），與本檔無關。
//
// 座標系：world coords（單機 myTeam=0，view==world）。
//   - 玩家可放列 = rows 6-7（基地 tile (3,7)(4,7) 不可佔）。
//   - AI 可放列  = rows 0-1（基地 tile (3,0)(4,0) 不可佔）。
// unit type 對應 UCFG key：Infantry / Cavalry / Archer / Mage。
// aiDeckId 對應 DeckTemplates：burn / storm / bulwark / tempest / starter。
// ============================================================

import type { DeployedUnit } from './Deployment.ts';

export interface LevelUnit {
  type: string;
  x: number;
  y: number;
}

export interface LevelDef {
  id: string;
  name: string;
  /** 一行關卡說明（難度 / AI 風格）。 */
  desc: string;
  /** 該關 AI 使用的牌組原型 id（DeckTemplates）。 */
  aiDeckId: string;
  /** 玩家固定單位與起始位置（rows 6-7）。 */
  playerUnits: LevelUnit[];
  /** AI 固定單位與起始位置（rows 0-1）。 */
  aiUnits: LevelUnit[];
}

export const LEVELS: LevelDef[] = [
  {
    id: 'lv1',
    name: '第一關 · 新兵演習',
    desc: '對手：元素均衡（無強 combo）。兵力少，適合熟悉出牌與路徑。',
    aiDeckId: 'starter',
    playerUnits: [
      { type: 'Infantry', x: 2, y: 7 },
      { type: 'Infantry', x: 5, y: 7 },
      { type: 'Archer', x: 3, y: 6 },
    ],
    aiUnits: [
      { type: 'Infantry', x: 2, y: 0 },
      { type: 'Infantry', x: 5, y: 0 },
    ],
  },
  {
    id: 'lv2',
    name: '第二關 · 沼澤前哨',
    desc: '對手：沼澤雷擊（積水加成雷擊 + 補盾）。留意積水地形。',
    aiDeckId: 'storm',
    playerUnits: [
      { type: 'Infantry', x: 2, y: 7 },
      { type: 'Infantry', x: 5, y: 7 },
      { type: 'Cavalry', x: 3, y: 6 },
      { type: 'Archer', x: 4, y: 6 },
    ],
    aiUnits: [
      { type: 'Infantry', x: 2, y: 0 },
      { type: 'Infantry', x: 5, y: 0 },
      { type: 'Archer', x: 3, y: 1 },
    ],
  },
  {
    id: 'lv3',
    name: '第三關 · 烈焰防線',
    desc: '對手：烈焰轟炸（長草點燃 DOT + 落石封路）。別擠在草地上。',
    aiDeckId: 'burn',
    playerUnits: [
      { type: 'Infantry', x: 1, y: 7 },
      { type: 'Infantry', x: 6, y: 7 },
      { type: 'Cavalry', x: 3, y: 6 },
      { type: 'Archer', x: 2, y: 6 },
      { type: 'Archer', x: 5, y: 6 },
    ],
    aiUnits: [
      { type: 'Infantry', x: 2, y: 0 },
      { type: 'Cavalry', x: 5, y: 0 },
      { type: 'Archer', x: 3, y: 1 },
    ],
  },
  {
    id: 'lv4',
    name: '第四關 · 鋼鐵壁壘',
    desc: '對手：鋼鐵壁壘（護盾補血 + 落石旋風拖延）。要打出持續輸出。',
    aiDeckId: 'bulwark',
    playerUnits: [
      { type: 'Infantry', x: 2, y: 7 },
      { type: 'Infantry', x: 5, y: 7 },
      { type: 'Cavalry', x: 3, y: 6 },
      { type: 'Archer', x: 1, y: 6 },
      { type: 'Archer', x: 6, y: 6 },
    ],
    aiUnits: [
      { type: 'Infantry', x: 1, y: 0 },
      { type: 'Infantry', x: 2, y: 0 },
      { type: 'Infantry', x: 5, y: 0 },
      { type: 'Archer', x: 6, y: 0 },
    ],
  },
  {
    id: 'lv5',
    name: '第五關 · 風暴決戰',
    desc: '對手：風暴操控（旋風位移 combo，上限最高）。最終考驗走位與時機。',
    aiDeckId: 'tempest',
    playerUnits: [
      { type: 'Infantry', x: 1, y: 7 },
      { type: 'Infantry', x: 6, y: 7 },
      { type: 'Cavalry', x: 2, y: 6 },
      { type: 'Cavalry', x: 5, y: 6 },
      { type: 'Archer', x: 3, y: 6 },
      { type: 'Archer', x: 4, y: 6 },
    ],
    aiUnits: [
      { type: 'Infantry', x: 1, y: 0 },
      { type: 'Infantry', x: 6, y: 0 },
      { type: 'Cavalry', x: 2, y: 0 },
      { type: 'Cavalry', x: 5, y: 0 },
      { type: 'Archer', x: 2, y: 1 },
      { type: 'Archer', x: 5, y: 1 },
    ],
  },
];

export function getLevel(id: string): LevelDef | undefined {
  return LEVELS.find((l) => l.id === id);
}

/** 關卡玩家單位 → Deployment.loadFromUnits 需要的 DeployedUnit[]（路徑留空，玩家自畫）。 */
export function levelPlayerDeployed(lv: LevelDef): DeployedUnit[] {
  return lv.playerUnits.map((u) => ({ type: u.type, pos: { x: u.x, y: u.y }, path: [] }));
}
